import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { HiCheck, HiChevronDown, HiOutlinePlus, HiOutlineCog, HiSearch } from "react-icons/hi";
import { getAllApps, getSelectedApp, setSelectedApp } from "../../../../services/app/app-service";
import { useAppInfo } from "../../../../helpers/hooks/common-hook";
import AppSaveDialog from "../../../agent-manage/agent-save-modal";
import { cn } from "../../../../helpers/utils/css-utils";
import { copilotPathFor } from "../../../copilot_native/model/copilot-paths";

const mapsStateToProps = (state) => ({
  appCreateNotifyMessage: state.appCreateNotifyMessage,
});

function AppSwitcher({ appCreateNotifyMessage }) {
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;
  const appInfo = useAppInfo();
  const [apps, setApps] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const [loadError, setLoadError] = useState("");
  const appRequestRef = useRef(0);

  const fetchApps = async () => {
    const requestId = ++appRequestRef.current;
    setLoadError("");
    let result;
    try {
      result = await getAllApps({ throwOnError: true });
    } catch (_) {
      if (requestId !== appRequestRef.current) return;
      setLoadError("Unable to load your applications. Please try again.");
      return;
    }
    if (requestId !== appRequestRef.current) return;
    setApps(result);

    if (result.length === 0) {
      setShowCreateModal(true);
      return;
    }

    // Only auto-navigate if on the root page
    if (locationRef.current.pathname === '/') {
      // Prefer saved app from localStorage, fallback to first app
      const savedAppId = getSelectedApp();
      if (savedAppId) {
        const savedApp = result.find(app => String(app.appId) === String(savedAppId));
        if (savedApp) {
          setSelectedApp(savedApp.appId);
          navigate(copilotPathFor(savedApp.appId), { replace: true });
          return;
        }
      }
      // No saved app or saved app not found — select first
      setSelectedApp(result[0].appId);
      navigate(copilotPathFor(result[0].appId), { replace: true });
    }
  };

  useEffect(() => {
    fetchApps();
  }, []);

  useEffect(() => {
    if (appCreateNotifyMessage) {
      fetchApps();
    }
  }, [appCreateNotifyMessage]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const filteredApps = apps.filter((app) =>
    app.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const navigateToApp = (app) => {
    setSelectedApp(app.appId);
    setIsOpen(false);
    window.location = copilotPathFor(app.appId);
  };

  const handleCreateClick = () => {
    setIsOpen(false);
    setShowCreateModal(true);
  };

  const handleAppManagerClick = () => {
    setIsOpen(false);
    navigate("/apps-manage");
  };

  const handleCreateCancel = () => {
    // Only allow closing if there are existing apps
    if (apps.length > 0) {
      setShowCreateModal(false);
    }
  };

  const handleCreateComplete = () => {
    setShowCreateModal(false);
  };

  const currentAppName = appInfo?.name || "Select Application";

  return (
    <>
      {loadError && (
        <div role="alert" className="text-sm text-rose-600">
          {loadError}
          <button type="button" onClick={fetchApps} className="ml-2 underline">Try again</button>
        </div>
      )}
      <div className="relative" ref={dropdownRef}>
        {/* Trigger Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1 transition-colors hover:bg-raised"
        >
          <span className="font-semibold text-sm tracking-wide text-ink truncate max-w-[120px]">
            {currentAppName}
          </span>
          <HiChevronDown
            className={cn(
              "w-4 h-4 text-muted transition-transform",
              { "rotate-180": isOpen }
            )}
          />
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute top-full left-0 mt-1 w-64 bg-panel rounded-lg border border-line shadow-lg z-50 overflow-hidden">
            {/* Header */}
            <div className="px-4 pt-3 pb-2">
              <p className="text-xs font-semibold text-muted uppercase tracking-wider">
                Applications
              </p>
            </div>

            {/* Search */}
            <div className="px-3 pb-2">
              <div className="relative">
                <HiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search apps..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-lg border border-line bg-raised/50 py-1.5 pl-8 pr-3 text-sm text-ink placeholder-muted transition-all duration-200 focus:border-accent focus:bg-panel focus:outline-none focus:ring-2 focus:ring-primary/10"
                />
              </div>
            </div>

            {/* App List */}
            <div className="max-h-64 overflow-y-auto">
              {filteredApps.map((app) => {
                const isSelected = appInfo?.appId === app.appId;
                return (
                  <button
                    key={app.appId}
                    onClick={() => navigateToApp(app)}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-raised/50 transition-colors",
                      { "bg-raised/50": isSelected }
                    )}
                  >
                    {isSelected ? (
                      <HiCheck className="w-4 h-4 text-ink flex-shrink-0" />
                    ) : (
                      <span className="w-4 h-4 flex-shrink-0" />
                    )}
                    <span className="text-sm text-ink truncate">
                      {app.name}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Divider + Create */}
            <div className="border-t border-line">
              <button
                onClick={handleCreateClick}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-raised/50 transition-colors"
              >
                <HiOutlinePlus className="w-4 h-4 text-muted flex-shrink-0" />
                <span className="text-sm text-ink/80">
                  Create Application
                </span>
              </button>
              <button
                onClick={handleAppManagerClick}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-raised/50 transition-colors"
              >
                <HiOutlineCog className="w-4 h-4 text-muted flex-shrink-0" />
                <span className="text-sm text-ink/80">
                  App Manager
                </span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create App Modal */}
      {showCreateModal && (
        <AppSaveDialog
          dismissible={apps.length > 0}
          onCompletion={handleCreateComplete}
          onCancel={handleCreateCancel}
        />
      )}
    </>
  );
}

export default connect(mapsStateToProps)(AppSwitcher);
